import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import Modal from './Modal';

export interface WebAlertButton {
  text: string;
  onPress?: () => void;
  style?: 'default' | 'cancel' | 'destructive';
}

interface AlertState {
  title: string;
  message?: string;
  buttons: WebAlertButton[];
}

let listener: ((state: AlertState | null) => void) | null = null;

export function showWebAlert(title: string, message?: string, buttons?: WebAlertButton[]): void {
  const resolvedButtons = buttons && buttons.length > 0 ? buttons : [{ text: 'OK' }];
  listener?.({ title, message, buttons: resolvedButtons });
}

const ConfirmDialog: React.FC = () => {
  const [state, setState] = useState<AlertState | null>(null);

  useEffect(() => {
    listener = setState;
    return () => { listener = null; };
  }, []);

  if (!state) return null;

  const handlePress = (btn: WebAlertButton) => {
    setState(null);
    btn.onPress?.();
  };

  return (
    <Modal visible transparent animationType="fade" onRequestClose={() => setState(null)}>
      <View style={styles.overlay}>
        <View style={styles.box}>
          <Text style={styles.title}>{state.title}</Text>
          {!!state.message && <Text style={styles.message}>{state.message}</Text>}
          <View style={styles.buttonRow}>
            {state.buttons.map((btn, i) => (
              <TouchableOpacity key={i} style={styles.button} onPress={() => handlePress(btn)}>
                <Text style={[
                  styles.buttonText,
                  btn.style === 'destructive' && styles.destructiveText,
                  btn.style === 'cancel' && styles.cancelText,
                ]}>
                  {btn.text}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default ConfirmDialog;

const styles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', alignItems: 'center', justifyContent: 'center', padding: 24 },
  box: { backgroundColor: '#fff', borderRadius: 16, padding: 20, width: '100%', maxWidth: 340 },
  title: { fontSize: 17, fontWeight: '700', color: '#1A1A1A', marginBottom: 6 },
  message: { fontSize: 14, color: '#444', marginBottom: 16, lineHeight: 20 },
  buttonRow: { flexDirection: 'row', justifyContent: 'flex-end', gap: 16, flexWrap: 'wrap' },
  button: { paddingVertical: 8, paddingHorizontal: 4 },
  buttonText: { fontSize: 15, fontWeight: '600', color: '#C2185B' },
  cancelText: { color: '#666' },
  destructiveText: { color: '#E74C3C' },
});
